import { OpponentResponseDto } from "@/lib/api/interfaces/opponents.interface";
import {
  useOpponentsApplyLogoEdit,
  useOpponentsDelete,
  useOpponentsDeleteLogo,
  useOpponentsFindOneQuery as useOpponentsFindOne,
  useOpponentsPreviewBackgroundRemoval,
  useOpponentsRestoreOriginalLogo,
  useOpponentsUpdate,
  useOpponentsUploadLogo,
} from "@/lib/api/requests/opponents.requests";
import { toast } from "@/lib/toast";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Input } from "@/ui-components/ui/input";
import { Skeleton } from "@/ui-components/ui/skeleton";
import { zodResolver } from "@hookform/resolvers/zod";
import { Calendar, Edit2, Flag, Radio } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import z from "zod";
import {
  DetailsBaseDialog,
  DetailsBaseDialogMode,
  DialogContentItem,
  DialogContentSection,
  DialogHeaderSection,
} from "@/util-components/dialog/base-dialogs";
import { ManagedImageBusyAction, ManagedImageField, persistManagedImageDraft, ManagedImageFieldSkeleton } from "@/util-components/media";

const updateOpponentSchema = z.object({
  name: z.string().min(1, { message: "Name ist erforderlich" }),
  image: z.instanceof(File).optional(),
});

type UpdateOpponentFormValues = z.infer<typeof updateOpponentSchema>;

const OpponentDetailsDialog = ({
  open,
  onClose,
  opponentId,
  mode = "details",
}: {
  open: boolean;
  onClose: (refetchData: boolean) => void;
  opponentId: string;
  mode?: DetailsBaseDialogMode;
}) => {
  const [isDeleteInProgress, setIsDeleteInProgress] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [canSave, setCanSave] = useState(false);
  const { data: opponent, isLoading } = useOpponentsFindOne(opponentId, {
    enabled: open && !!opponentId && !isDeleteInProgress,
  });
  const { mutateAsync: deleteOpponent, isPending: isDeleting } = useOpponentsDelete();

  if (!opponent && !isLoading) {
    return (
      <DetailsBaseDialog
        open={open}
        onClose={onClose}
        icon={Flag}
        title="Gegner-Details"
        description="Informationen zum Gegner einsehen und bearbeiten."
        deleteTitle="Gegner löschen"
        deleteItemTitle=""
        onDeleteConfirm={() => {}}
        detailsContent={<div className="py-12 text-center text-muted-foreground">Gegner nicht gefunden.</div>}
        editContent={() => null}
      />
    );
  }

  const handleDelete = async () => {
    if (!opponentId || isDeleteInProgress) return;
    setIsDeleteInProgress(true);

    try {
      await deleteOpponent({ id: opponentId });
      toast.success("Gegner wurde erfolgreich gelöscht");
      onClose(true);
    } catch (error) {
      toast.error("Gegner konnte nicht gelöscht werden", { error });
      throw error;
    } finally {
      setIsDeleteInProgress(false);
    }
  };

  return (
    <DetailsBaseDialog
      open={open}
      onClose={onClose}
      mode={mode}
      icon={Flag}
      title="Gegner-Details"
      description="Informationen zum Gegner einsehen und bearbeiten."
      deleteTitle="Gegner löschen"
      deleteItemTitle={`${opponent?.name}`}
      deleteItemTypeValue={opponentId}
      onDeleteConfirm={handleDelete}
      isDeleting={isDeleting || isDeleteInProgress}
      isSaving={isSaving}
      canSave={canSave}
      formId="edit-opponent-form"
      detailsContent={isLoading ? <OpponentDetailsSkeleton /> : <OpponentDetails opponent={opponent!} />}
      editContent={(onSaveSuccess) =>
        isLoading ? (
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <OpponentDetailsSkeleton />
          </div>
        ) : (
          <OpponentEditForm
            opponent={opponent!}
            onSaveSuccess={onSaveSuccess}
            setIsSaving={setIsSaving}
            setCanSave={setCanSave}
          />
        )
      }
    />
  );
};

const OpponentDetailsSkeleton = () => {
  return (
    <div className="space-y-6 overflow-y-auto">
      <ManagedImageFieldSkeleton />
      <div className="grid grid-cols-2 gap-4">
        <Skeleton className="h-20 w-full rounded-xl" />
        <Skeleton className="h-20 w-full rounded-xl" />
      </div>
    </div>
  );
};

const OpponentDetails = ({ opponent }: { opponent: OpponentResponseDto }) => {
  const activeLogoUrl = opponent.logoState?.active?.url || opponent.logoUrl;

  return (
    <>
      <DialogHeaderSection>
        <div className="flex flex-col items-center justify-center gap-4 text-center">
          <div className="relative flex h-24 w-24 items-center justify-center rounded-2xl bg-white p-3 shadow-md">
            {activeLogoUrl ? (
              <img src={activeLogoUrl} alt={opponent.name} className="h-full w-full object-contain" />
            ) : (
              <Flag className="h-10 w-10 text-gray-200" />
            )}
          </div>
          <div>
            <h2 className="text-2xl font-black tracking-tight text-white">{opponent.name}</h2>
          </div>
        </div>
      </DialogHeaderSection>

      <DialogContentSection title="Informationen" icon={Radio}>
        <div className="grid gap-3 sm:grid-cols-2">
          <DialogContentItem label="Erstellt am" value={new Date(opponent.createdAt).toLocaleDateString("de-DE")} icon={Calendar} />
          <DialogContentItem
            label="Zuletzt bearbeitet"
            value={new Date(opponent.updatedAt).toLocaleDateString("de-DE")}
            icon={Edit2}
          />
        </div>
      </DialogContentSection>
    </>
  );
};

const OpponentEditForm = ({
  onSaveSuccess,
  opponent,
  setIsSaving,
  setCanSave,
}: {
  onSaveSuccess: () => void;
  opponent: OpponentResponseDto;
  setIsSaving: (val: boolean) => void;
  setCanSave: (val: boolean) => void;
}) => {
  const { mutateAsync: updateOpponent } = useOpponentsUpdate();
  const { mutateAsync: uploadLogo } = useOpponentsUploadLogo();
  const { mutateAsync: deleteLogo } = useOpponentsDeleteLogo();
  const { mutateAsync: applyLogoEdit } = useOpponentsApplyLogoEdit();
  const { mutateAsync: restoreOriginalLogo } = useOpponentsRestoreOriginalLogo();
  const { mutateAsync: previewBackgroundRemoval } = useOpponentsPreviewBackgroundRemoval();
  const [mediaBusyAction, setMediaBusyAction] = useState<ManagedImageBusyAction>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<UpdateOpponentFormValues>({
    resolver: zodResolver(updateOpponentSchema),
    mode: "onChange",
    defaultValues: {
      name: opponent.name,
      image: undefined,
    },
  });

  const watchedValues = form.watch();
  const selectedImageFile = watchedValues.image;

  useEffect(() => {
    setIsSaving(isSubmitting || mediaBusyAction !== null);
  }, [isSubmitting, mediaBusyAction, setIsSaving]);

  const hasChanges = useMemo(
    () => watchedValues.name !== opponent.name || !!watchedValues.image,
    [opponent.name, watchedValues.image, watchedValues.name]
  );

  useEffect(() => {
    setCanSave(form.formState.isValid && hasChanges && mediaBusyAction === null);
  }, [form.formState.isValid, hasChanges, mediaBusyAction, setCanSave]);

  const runMediaAction = async ({
    action,
    task,
    successMessage,
    errorMessage,
  }: {
    action: Exclude<ManagedImageBusyAction, null>;
    task: () => Promise<unknown>;
    successMessage: string;
    errorMessage: string;
  }) => {
    setMediaBusyAction(action);

    try {
      await task();
      toast.success(successMessage);
    } catch (error) {
      toast.error(errorMessage, { error });
    } finally {
      setMediaBusyAction(null);
    }
  };

  const onSubmit = async (data: UpdateOpponentFormValues) => {
    setIsSubmitting(true);

    try {
      await updateOpponent({
        id: opponent._id,
        data: { name: data.name },
      });

      await persistManagedImageDraft({
        entityId: opponent._id,
        file: data.image,
        uploadImage: uploadLogo,
        applyImageEdit: applyLogoEdit,
      });

      toast.success("Gegner wurde erfolgreich aktualisiert");
      onSaveSuccess();
    } catch (error) {
      toast.error("Gegner konnte nicht gespeichert werden", { error });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form id="edit-opponent-form" onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex-1 space-y-6 overflow-y-auto p-6">
          <FormField
            control={form.control}
            name="image"
            render={() => (
              <FormItem>
                <FormControl>
                  <ManagedImageField
                    value={opponent.logoState}
                    file={selectedImageFile}
                    onFileChange={(nextFile) => form.setValue("image", nextFile ?? undefined, { shouldDirty: true, shouldValidate: true })}
                    onDeleteRemote={() =>
                      runMediaAction({
                        action: "delete",
                        task: () => deleteLogo({ id: opponent._id }),
                        successMessage: "Wappen wurde entfernt",
                        errorMessage: "Wappen konnte nicht entfernt werden",
                      })
                    }
                    onApplyEdit={(edit) =>
                      runMediaAction({
                        action: "applyEdit",
                        task: () => applyLogoEdit({ id: opponent._id, data: edit }),
                        successMessage: "Bildbearbeitung wurde übernommen",
                        errorMessage: "Wappen konnte nicht bearbeitet werden",
                      })
                    }
                    onRestoreOriginal={() =>
                      runMediaAction({
                        action: "restoreOriginal",
                        task: () => restoreOriginalLogo({ id: opponent._id }),
                        successMessage: "Originalwappen wurde wiederhergestellt",
                        errorMessage: "Originalwappen konnte nicht wiederhergestellt werden",
                      })
                    }
                    disabled={isSubmitting}
                    busyAction={isSubmitting ? "saving" : mediaBusyAction}
                    emptyTitle="Wappen auswählen"
                    previewBackgroundRemoval={async (file) => previewBackgroundRemoval({ file })}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel className="text-sm font-medium text-gray-700">
                  Vereinsname <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="FC Bayern"
                    {...field}
                    className="h-11 border-gray-200 transition-all focus:border-blue-500 focus:ring-blue-500/20"
                    disabled={isSubmitting}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </form>
    </Form>
  );
};

export default OpponentDetailsDialog;
